import { useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CaretDown, Check, FloppyDisk, PencilSimple, Sliders, Sparkle, Trash, X } from '@phosphor-icons/react';
import {
  createComposerMediaModelPreset,
  deleteComposerMediaModelPreset,
  getComposerMediaModels,
  listComposerMediaModelPresets,
  updateComposerMediaModelPreset,
} from '../../lib/api';
import { COMPOSER_LLM_MODELS, DEFAULT_COMPOSER_LLM_MODEL } from '../../composerModels';
import './MediaModelPicker.css';

let catalogPromise;
const loadCatalog = () => {
  catalogPromise ||= getComposerMediaModels().catch((error) => {
    catalogPromise = null;
    throw error;
  });
  return catalogPromise;
};

const snapshotOf = (value) => value ? ({
  defaultVideoModel: value.defaultVideoModel,
  availableVideoModels: [...(value.availableVideoModels || [])],
  defaultImageModel: value.defaultImageModel,
  availableImageModels: [...(value.availableImageModels || [])],
  schemaVersion: value.schemaVersion || 1,
}) : null;

const sameSnapshot = (a, b) => JSON.stringify(snapshotOf(a)) === JSON.stringify(snapshotOf(b));

/**
 * Map a picker selection onto compose request options.
 *
 * The four model fields drive execution; the preset id and source label are
 * audit only, and the server drops an id the signed-in user does not own. A
 * null selection means Auto and contributes no fields at all.
 */
export const mediaModelRequestOptions = (selection) => selection ? ({
  ...snapshotOf(selection),
  mediaModelPresetId: selection.presetId || selection.sourcePresetId || null,
  mediaModelSelectionSource: selection.selectionSource || null,
}) : {};

function ModelGroup({ title, models, selected, defaultModel, onToggle, onDefault }) {
  return <fieldset className="media-model-picker__group">
    <legend>{title}</legend>
    {models.map((model) => {
      const checked = selected.includes(model.id);
      return <div className="media-model-picker__model" key={model.id}>
        <label>
          <input type="checkbox" checked={checked} disabled={!model.enabled} onChange={() => onToggle(model.id)} />
          <span><strong>{model.name}</strong><small>{model.description}</small></span>
        </label>
        <label className="media-model-picker__default">
          <input type="radio" name={`default-${title}`} checked={defaultModel === model.id} disabled={!checked} onChange={() => onDefault(model.id)} />
          Default
        </label>
      </div>;
    })}
  </fieldset>;
}

export default function MediaModelPicker({
  value,
  onChange,
  llmModel = DEFAULT_COMPOSER_LLM_MODEL,
  onLlmModelChange,
  onEvent,
  disabled = false,
  triggerClassName = '',
  embedded = false,
}) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [presets, setPresets] = useState([]);
  const [draft, setDraft] = useState(snapshotOf(value));
  const [presetName, setPresetName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => setDraft(snapshotOf(value)), [value]);
  useEffect(() => {
    if (!embedded && !open && !value?.presetId) return;
    let cancelled = false;
    setError('');
    loadCatalog()
      .then((nextCatalog) => {
        if (cancelled) return;
        setCatalog(nextCatalog);
      })
      .catch(() => !cancelled && setError('Could not load media models.'));
    listComposerMediaModelPresets()
      .then((result) => {
        if (!cancelled) setPresets(result.presets || []);
      })
      .catch(() => {
        if (!cancelled) setPresets([]);
      });
    return () => { cancelled = true; };
  }, [embedded, open, value?.presetId]);

  const beginCustom = () => setDraft(snapshotOf(value) || (catalog ? {
    defaultVideoModel: catalog.autoDefaults.video,
    availableVideoModels: catalog.video.filter((item) => item.enabled).map((item) => item.id),
    defaultImageModel: catalog.autoDefaults.image,
    availableImageModels: catalog.image.filter((item) => item.enabled).map((item) => item.id),
    schemaVersion: catalog.schemaVersion,
  } : null));

  const toggle = (kind, modelId) => setDraft((current) => {
    if (!current) return current;
    const listKey = kind === 'video' ? 'availableVideoModels' : 'availableImageModels';
    const defaultKey = kind === 'video' ? 'defaultVideoModel' : 'defaultImageModel';
    const selected = current[listKey];
    if (selected.includes(modelId) && selected.length === 1) { setAnnouncement(`At least one ${kind} model is required.`); return current; }
    const next = selected.includes(modelId) ? selected.filter((id) => id !== modelId) : [...selected, modelId];
    setAnnouncement(`${modelId} ${selected.includes(modelId) ? 'removed' : 'selected'}.`);
    return { ...current, [listKey]: next, [defaultKey]: next.includes(current[defaultKey]) ? current[defaultKey] : next[0] };
  });

  const selectedPreset = useMemo(
    () => presets.find((preset) => preset.id === value?.presetId),
    [presets, value?.presetId],
  );
  const presetUnavailable = value?.presetId && catalog && !selectedPreset;
  const presetChanged = value?.presetId && selectedPreset && !sameSnapshot(value, selectedPreset);
  // The composer toolbar is a tight row, so drift shows there only as a dot;
  // the explanation and the repair action live inside the panel.
  const drift = presetUnavailable
    ? { message: 'The saved set was deleted. This run still uses your last applied copy.' }
    : presetChanged
      ? { message: 'This set changed since you applied it.', repairable: true }
      : null;
  const label = value?.selectionSource === 'saved' && !presetChanged
    ? selectedPreset?.name || value.presetName || 'Saved set'
    : value ? `Custom ${value.availableVideoModels?.length || 0}V · ${value.availableImageModels?.length || 0}I` : 'Auto';
  const customIsApplied = Boolean(value) && value.selectionSource !== 'saved';
  const selectedLlmModel = COMPOSER_LLM_MODELS.find((item) => item.value === llmModel)
    || COMPOSER_LLM_MODELS[0];

  const choosePreset = (preset) => {
    setDraft(snapshotOf(preset));
    onChange({ ...snapshotOf(preset), presetId: preset.id, presetName: preset.name, selectionSource: 'saved' });
    onEvent?.('composer_model_preset_selected', { video_count: preset.availableVideoModels.length, image_count: preset.availableImageModels.length });
    setOpen(false);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!draft || !name) return;
    setBusy(true); setError('');
    try {
      const created = await createComposerMediaModelPreset({ name, ...draft });
      setPresets((current) => [created, ...current]);
      setPresetName('');
      onChange({ ...snapshotOf(created), presetId: created.id, presetName: created.name, selectionSource: 'saved' });
      onEvent?.('composer_model_preset_saved', { video_count: created.availableVideoModels.length, image_count: created.availableImageModels.length });
      setOpen(false);
    } catch (err) { setError('Could not save this set.'); }
    finally { setBusy(false); }
  };

  const saveChanges = async () => {
    if (!draft || !selectedPreset) return;
    setBusy(true); setError('');
    try {
      const updated = await updateComposerMediaModelPreset(selectedPreset.id, { ...draft, name: selectedPreset.name });
      setPresets((current) => current.map((item) => item.id === updated.id ? updated : item));
      onChange({ ...snapshotOf(updated), presetId: updated.id, presetName: updated.name, selectionSource: 'saved' });
      onEvent?.('composer_model_preset_saved', { video_count: updated.availableVideoModels.length, image_count: updated.availableImageModels.length, updated: true });
      setOpen(false);
    } catch (err) { setError('Could not save these changes.'); }
    finally { setBusy(false); }
  };

  const renamePreset = async (preset) => {
    const name = window.prompt('Rename media model set', preset.name)?.trim();
    if (!name || name === preset.name) return;
    setBusy(true); setError('');
    try {
      const updated = await updateComposerMediaModelPreset(preset.id, { ...snapshotOf(preset), name });
      setPresets((current) => current.map((item) => item.id === preset.id ? updated : item));
      if (value?.presetId === preset.id) onChange({ ...snapshotOf(updated), presetId: updated.id, presetName: updated.name, selectionSource: 'saved' });
    } catch (err) { setError('Could not rename this set.'); }
    finally { setBusy(false); }
  };

  const removePreset = async (preset) => {
    if (!window.confirm(`Delete “${preset.name}”? Workflows keep their saved model snapshot.`)) return;
    setBusy(true); setError('');
    try {
      await deleteComposerMediaModelPreset(preset.id);
      setPresets((current) => current.filter((item) => item.id !== preset.id));
      if (value?.presetId === preset.id) onChange({ ...snapshotOf(value), presetId: null, presetName: null, selectionSource: 'custom' });
      onEvent?.('composer_model_preset_deleted', {});
    } catch (err) { setError('Could not delete this set.'); }
    finally { setBusy(false); }
  };

  const panel = <div className={`media-model-picker${embedded ? ' media-model-picker--embedded' : ''}`}>
        {!embedded && <header><div><strong>Model settings</strong><small>Choose the Composer, video, and image models for this run.</small></div><Popover.Close aria-label="Close"><X size={15} /></Popover.Close></header>}
        {drift && <div className="media-model-picker__drift" role="status">
          <span>{drift.message}</span>
          {drift.repairable && <button type="button" onClick={() => choosePreset(selectedPreset)}>Update from set</button>}
        </div>}
        {onLlmModelChange && <label className="media-model-picker__llm">
          <span><strong>Composer model</strong><small>The LLM that plans and edits your project</small></span>
          <select
            aria-label="Composer model"
            value={selectedLlmModel.value}
            onChange={(event) => {
              onLlmModelChange?.(event.target.value);
              onEvent?.('composer_llm_model_selected', { model: event.target.value });
            }}
          >
            {['OpenAI', 'Anthropic'].map((provider) => (
              <optgroup label={provider} key={provider}>
                {COMPOSER_LLM_MODELS.filter((item) => item.provider === provider).map((item) => (
                  <option value={item.value} key={item.value}>{item.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>}
        <div className="media-model-picker__quick">
          <button type="button" className={!value ? 'is-selected' : ''} onClick={() => { onChange(null); setDraft(null); onEvent?.('composer_model_selection_applied', { selection: 'auto', video_count: 0, image_count: 0 }); setOpen(false); }}><span><strong>Auto</strong><small>Let Composer choose from all enabled models</small></span>{!value && <Check size={14} />}</button>
          {presets.map((preset) => <div className="media-model-picker__preset" key={preset.id}>
            <button type="button" onClick={() => choosePreset(preset)}><span><strong>{preset.name}</strong><small>{preset.availableVideoModels.length} video · {preset.availableImageModels.length} image</small></span>{value?.presetId === preset.id && <Check size={14} />}</button>
            <button type="button" aria-label={`Rename ${preset.name}`} onClick={() => renamePreset(preset)}><PencilSimple size={13} /></button>
            <button type="button" aria-label={`Delete ${preset.name}`} onClick={() => removePreset(preset)}><Trash size={13} /></button>
          </div>)}
        </div>
        <button
          type="button"
          className={`media-model-picker__custom${draft ? ' is-open' : ''}${customIsApplied ? ' is-applied' : ''}`}
          onClick={beginCustom}
          aria-expanded={Boolean(draft)}
        >
          <Sliders size={14} weight="bold" aria-hidden="true" />
          <span><strong>Custom</strong><small>Choose an allowlist and defaults</small></span>
          {customIsApplied && <Check size={14} weight="bold" aria-hidden="true" />}
          <CaretDown size={12} weight="bold" aria-hidden="true" />
        </button>
        {draft && catalog && <div className="media-model-picker__editor">
          <ModelGroup title="Video models" models={catalog.video} selected={draft.availableVideoModels} defaultModel={draft.defaultVideoModel} onToggle={(id) => toggle('video', id)} onDefault={(id) => setDraft((current) => ({ ...current, defaultVideoModel: id }))} />
          <ModelGroup title="Image models" models={catalog.image} selected={draft.availableImageModels} defaultModel={draft.defaultImageModel} onToggle={(id) => toggle('image', id)} onDefault={(id) => setDraft((current) => ({ ...current, defaultImageModel: id }))} />
          <div className="media-model-picker__save"><input value={presetName} maxLength={80} placeholder="Save as a new set…" onChange={(event) => setPresetName(event.target.value)} /><button type="button" disabled={busy || !presetName.trim()} onClick={savePreset}><FloppyDisk size={13} /> Save</button></div>
          {selectedPreset && <button type="button" className="media-model-picker__apply" disabled={busy || sameSnapshot(draft, selectedPreset)} onClick={saveChanges}>Save changes to {selectedPreset.name}</button>}
          <button type="button" className="media-model-picker__apply" disabled={busy || sameSnapshot(draft, value)} onClick={() => { onChange({ ...draft, presetId: null, presetName: null, sourcePresetId: value?.presetId || value?.sourcePresetId || null, sourcePresetName: value?.presetName || value?.sourcePresetName || null, selectionSource: 'custom' }); onEvent?.('composer_model_selection_applied', { selection: 'custom', video_count: draft.availableVideoModels.length, image_count: draft.availableImageModels.length }); setOpen(false); }}>Apply custom set</button>
        </div>}
        {!catalog && !error && <p className="media-model-picker__status">Loading media models…</p>}
        {error && <p className="media-model-picker__error" role="alert">{error}</p>}
        <span className="media-model-picker__sr" aria-live="polite">{announcement}</span>
      </div>;

  if (embedded) return panel;

  return <Popover.Root open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (nextOpen) onEvent?.('composer_model_picker_opened', {}); }}>
    <Popover.Trigger asChild>
      <button
        type="button"
        className={`media-model-picker__trigger ${triggerClassName || 'media-model-picker__trigger--standalone'}`}
        disabled={disabled}
        aria-label={`Media models: ${label}${drift ? `. ${drift.message}` : ''}`}
      >
        <Sparkle size={14} weight="bold" aria-hidden="true" />
        <span className="media-model-picker__trigger-label">
          <span className="media-model-picker__trigger-prefix">Models</span>
          <span className="media-model-picker__trigger-value">{label}</span>
        </span>
        {drift && <span className="media-model-picker__dot" aria-hidden="true" />}
        <CaretDown size={11} weight="bold" aria-hidden="true" />
      </button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content asChild sideOffset={7} align="start" collisionPadding={12}>
        {panel}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}
